import { Button, Dialog, Stack } from "@mui/material";
import { FilterView, LogModel } from "../../services/swagger";
import FieldLabel from "../atoms/FieldLabel";
import FormInput from "../molecules/FormInput";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FilterViewService } from "../../services/api";
import { useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import useConfirm from "../../hooks/useConfirm";
import { useSearchParams } from "react-router-dom";
import {
  filtersToParamString,
  paramStringToObject,
} from "../../helpers/params";
import FormTemplate from "../templates/FormTemplate";

interface FilterViewsProps {
  model: LogModel;
  filterView: string;
  onChange: (filterView: FilterView) => any;
}

export default function FilterViews(props: FilterViewsProps) {
  const { t } = useTranslation("FilterViews");
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [createDialog, setCreateDialog] = useState(false);

  const defaultFilterView: FilterView = useMemo(() => {
    return {
      filters: [],
      model: props.model,
      name: t("DefaultView"),
      search: "",
      user: "",
      _id: "",
    };
  }, [props.model]);

  const filters = useMemo(
    () =>
      paramStringToObject(
        searchParams.get("filters") || filtersToParamString([])
      ),
    [searchParams]
  );

  const search = useMemo(
    () => searchParams.get("search") || "",
    [searchParams]
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["filter-views", { model: props.model }],
    queryFn: async () => {
      const res = await FilterViewService.getMyFilterViewList({
        model: props.model,
      });
      const filterViews = res?.data?.data || [];
      const exists = Boolean(
        filterViews.find((fv) => fv._id === props.filterView)
      );
      if (!exists) props.onChange(defaultFilterView);
      return filterViews;
    },
    initialData: [],
  });

  const currentFilterView = useMemo(() => {
    const filterView = data.find((fv) => fv._id === props.filterView);
    const defaultFilterView: FilterView = {
      filters: [],
      model: props.model,
      name: t("DefaultView"),
      search: "",
      user: "",
      _id: "",
    };
    return filterView || defaultFilterView;
  }, [data, props.filterView, props.model]);

  const { mutate: createView } = useMutation({
    mutationFn: async () => {
      const res = await FilterViewService.createFilterView({
        createFilterViewBody: {
          filters,
          model: props.model,
          name,
          search,
        },
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) {
        refetch();
        props.onChange(data);
        handleCloseCreateDialog();
      }
    },
  });

  const { mutate: saveView } = useMutation({
    mutationFn: async () => {
      if (!currentFilterView) return;
      const res = await FilterViewService.updateFilterView({
        id: props.filterView,
        updateFilterViewBody: {
          filters,
          model: props.model,
          name: currentFilterView?.name,
          search,
        },
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) refetch();
    },
  });

  const { mutate: deleteView } = useMutation({
    mutationFn: async () => {
      if (!currentFilterView) return;
      const res = await FilterViewService.deleteFilterView({
        id: props.filterView,
      });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) refetch();
    },
  });

  const filterViewList = useMemo(() => {
    const items = data.map((e) => ({
      name: e.name,
      value: e._id || "",
    }));
    return [{ name: t("DefaultView"), value: "" }, ...items];
  }, [data]);

  const handleChange = (_id: string) => {
    const filterView = data.find((fv) => fv._id === _id);

    props.onChange(filterView || defaultFilterView);
  };

  const handleOpenCreateDialog = () => {
    setCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setName("");
    setCreateDialog(false);
  };

  const handleSave = () => {
    confirm(saveView);
  };

  const handleDelete = () => {
    confirm(deleteView);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FieldLabel>{t("FilterView")}</FieldLabel>
        <FormInput
          field="filterView"
          label=""
          type="list"
          list={filterViewList}
          value={currentFilterView?._id}
          onChange={(v) => handleChange(v as string)}
          loading={isLoading}
        />
      </Stack>
      <Dialog open={createDialog} onClose={handleCloseCreateDialog}>
        <FormTemplate
          title={t("SaveView")}
          onSubmit={createView}
          onCancel={handleCloseCreateDialog}
          confirm
        >
          <FormInput
            required
            field="name"
            label={t("Name")}
            type="string"
            value={name}
            onChange={(v) => setName(v as string)}
          />
        </FormTemplate>
      </Dialog>
      <Stack direction="row" justifyContent="flex-end">
        {props.filterView && (
          <Button onClick={handleDelete} color="error">
            {t("Delete")}
          </Button>
        )}
        {props.filterView && <Button onClick={handleSave}>{t("Save")}</Button>}
        <Button endIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          {t("New")}
        </Button>
      </Stack>
    </Stack>
  );
}
